import {zendeskCategoryConfig} from 'common/src/modules/help/help.config';
import {KnowledgeCenterTab} from 'common/src/modules/help/help.types';
import ragSearchApi from 'common/src/modules/ragSearch/ragSearch.api';
import {EmbeddingType} from 'common/src/modules/ragSearch/ragSearch.enums';
import {Routes} from 'common/src/modules/routing/routing.config';
import {AppRoute} from 'common/src/modules/routing/routing.types';
import getArticleSlugByName from 'common/src/modules/zendesk/utils/getArticleSlugByName';
import {CLONED_DRAFT_PREFIX} from 'common/src/modules/zendesk/zendesk.consts';
import {RagArticle} from 'common/src/modules/zendesk/zendesk.types';
import {store} from 'common/src/redux/store';
import {
  FORCE_HIDE_COPILOT_SEARCH_PARAM,
  ragNumberOfResults,
  ragSimilarityThreshold,
} from '../copilot.consts';
import {ToolResultPayloadType} from '../copilot.enums';
import {Article, ToolResult} from '../copilot.types';
import openAISystemMessage from '../openAI/utils/openAISystemMessage';
import openAIUserMessage from '../openAI/utils/openAIUserMessage';
import invokeStandaloneLLMCall from './invokeStandaloneLLMCall';
import {logger} from './logger';
import {ragSearchResponseSchema} from '../copilot.schemas';

async function invokeRagArticleSearch({
  prompt,
  context,
  embeddingTypes,
  abortSignal,
}: {
  prompt: string;
  context?: string;
  embeddingTypes: EmbeddingType[];
  abortSignal: AbortSignal;
}): Promise<ToolResult> {
  const similarArticles = await ragSearchApi.getSimilarArticles({
    prompt,
    embeddingTypes,
    similarityThreshold: ragSimilarityThreshold,
    numOfResults: ragNumberOfResults,
    includeLiveDrafts: store.getState().copilot.settings.isEditArticlesEnabled,
  });

  const similarArticlesString = similarArticles
    .map(({minifiedArticle}) => `### ${minifiedArticle.title}\n\n${minifiedArticle.minifiedBody}`)
    .join('\n\n');

  const instructions = `You are an expert on the Flight Control product.

You answer user questions **based only on the provided context and articles below**. These articles are from the Knowledge Center.

### Strict rules:

1. **Only use the provided context and articles to answer.**  
   - Do **not** use your own knowledge.  
   - Do **not** add explanations, assumptions, examples, suggestions, or code - even if the context and articles don't fully answer the question.

2. If the context or articles do **not** directly answer the question but contain **related information**, such as similar features, related integrations, or adjacent functionality, **you MUST include that information**.  
   - This helps the user understand what **is** supported, even if their exact request isn't documented.

3. NEVER mention "context" in your answer - this is an internal detail. Use the term **"Knowledge Center"** instead.

4. If the question is vague or unclear, ask the user to clarify.

5. Respond in an easy-to-read structured **markdown** format. Paragraphs should be short and concise.

Respond **only** using the exact language and facts found in the context and articles. **Never go beyond it under any circumstances.**
  
## Context

${context || 'N/A'}

## Articles
  
${similarArticlesString}
`;

  const messages = [openAISystemMessage(instructions), openAIUserMessage(prompt)];

  const responseObject = await invokeStandaloneLLMCall({
    messages,
    responseSchema: ragSearchResponseSchema,
    abortSignal,
  });

  const articles = similarArticles
    .filter((article) => article.showToUser)
    .map(_buildArticle)
    .filter(Boolean) as Article[];
  const {answer, answerType} = responseObject;

  return {
    content: answer,
    payload: {
      type: ToolResultPayloadType.Knowledge,
      answerType,
      articles,
    },
  };
}

function _buildArticle(searchResult: RagArticle): Article | null {
  const {
    id: articleId,
    similarityScore,
    minifiedArticle: {title, category: articleCategory},
  } = searchResult;

  let isDraft = false;
  let publishedTitle = title;

  if (title.startsWith(CLONED_DRAFT_PREFIX)) {
    publishedTitle = title.split('|')[1];
    isDraft = true;
  }

  const knowlegeCenterTab = zendeskCategoryConfig.find(
    (category) => category.category === articleCategory,
  )?.tab;

  if (!knowlegeCenterTab) {
    logger.error('RAG search: No knowledge center tab found for article', searchResult);

    return null;
  }

  const article: Article = {
    title: publishedTitle,
    link: _buildArticleLink({articleId, title, knowlegeCenterTab}),
    relevance: similarityScore,
    articleId,
    isDraft,
  };

  return article;
}

function _buildArticleLink({
  articleId,
  title,
  knowlegeCenterTab,
}: {
  articleId: number;
  title: string;
  knowlegeCenterTab: KnowledgeCenterTab;
}): AppRoute {
  return {
    url: Routes.KnowledgeCenterArticle,
    pathParams: {
      articleId,
      slug: getArticleSlugByName(title),
    },
    queryParams: {
      tab: knowlegeCenterTab,
      [FORCE_HIDE_COPILOT_SEARCH_PARAM]: 'true',
    },
  };
}

export default invokeRagArticleSearch;
